/**
 * Stores: the consumables store (stickers, rolls, pouches, bags, tags, chemicals), after seedtrac
 * STORES. Stock is counted twice over, in UPS (units) and in Qty (the item's UoM), and kept apart as
 * Good and Damage in each leaf bin.
 *
 * This file holds what the page and the API share for the Arrival transactions:
 *
 *  - the four arrival kinds and what each one's header asks for;
 *  - Excess / Shortage, worked out from the DC and the received Good + Damage;
 *  - the Preview blockers, including the put-away rule (the SLOC split must add up exactly).
 *
 *     draft ──preview──▶ previewed ──final submit──▶ submitted (number minted, stock posted)
 *       ▲                   │                            │
 *       └───────edit────────┘                            └──cancel──▶ cancelled (stock reversed)
 */
import { toDecimal, type DecimalInput } from "./decimal";
import {
  missingDispatchTransport,
  type DispatchTransport,
} from "./dispatch-stock-transfer";

export const STORES_ARRIVAL_KINDS = [
  "arrival_vendor",
  "arrival_stock_transfer",
  "return_own",
  "return_external",
] as const;
export type StoresArrivalKind = (typeof STORES_ARRIVAL_KINDS)[number];

/** Who the stock comes from: a Stores vendor, another of our plants, this plant, or a C&F/dealer. */
export type StoresPartySource =
  "vendor" | "plant" | "own_plant" | "stores_party";

export interface StoresArrivalKindConfig {
  kind: StoresArrivalKind;
  /** The list's pill and the page title. */
  label: string;
  title: string;
  /**
   * Number prefix, NexSeed style: SAV/0001/Y/D. Every one starts with S so the series never meets a
   * seed form's (AS is Stock Transfer In, AT Trading, ...); a test pins that.
   */
  prefix: string;
  /** The printed note: its short code (the list's Output column) and its heading. */
  printCode: string;
  printTitle: string;
  party: StoresPartySource;
  /** UPS / Qty "as per D.C." — the vendor's or sending plant's paper. Returns have none. */
  hasDc: boolean;
  /** External Party returns are damage only. */
  allowGood: boolean;
  hasTransit: boolean;
  /** The permission that raises (and edits, previews, submits) this kind. */
  permission: string;
}

export const STORES_ARRIVALS: Record<
  StoresArrivalKind,
  StoresArrivalKindConfig
> = {
  arrival_vendor: {
    kind: "arrival_vendor",
    label: "From Vendor",
    title: "Arrival from Vendor",
    prefix: "SAV",
    printCode: "GRN",
    printTitle: "Goods Receipt Note (GRN)",
    party: "vendor",
    hasDc: true,
    allowGood: true,
    hasTransit: true,
    permission: "stores.arrival.vendor",
  },
  arrival_stock_transfer: {
    kind: "arrival_stock_transfer",
    label: "From Stock Transfer",
    title: "Arrival from Stock Transfer",
    prefix: "SAS",
    printCode: "STRN",
    printTitle: "Stock Transfer Receipt Note (STRN)",
    party: "plant",
    hasDc: true,
    allowGood: true,
    hasTransit: true,
    permission: "stores.arrival.stock_transfer",
  },
  return_own: {
    kind: "return_own",
    label: "Internal Return – Own",
    title: "Internal Return – Own (Good & Damage)",
    prefix: "SAI",
    printCode: "IMRN-O",
    printTitle: "Internal Material Return Note - Own (IMRN-O)",
    party: "own_plant",
    hasDc: false,
    allowGood: true,
    hasTransit: false,
    permission: "stores.arrival.return",
  },
  return_external: {
    kind: "return_external",
    label: "Internal Return – External Party",
    title: "Internal Return – External Party (Damage)",
    prefix: "SAI",
    printCode: "IMRN-P",
    printTitle: "Internal Material Return Note - Party (IMRN-P)",
    party: "stores_party",
    hasDc: false,
    allowGood: false,
    hasTransit: false,
    permission: "stores.arrival.return",
  },
};

export const storesArrival = (
  kind: StoresArrivalKind
): StoresArrivalKindConfig => STORES_ARRIVALS[kind];

/** Every number prefix Stores mints. Disjoint from the seed forms' prefixes (pinned by a test). */
export const STORES_NUMBER_PREFIXES = [
  ...new Set(Object.values(STORES_ARRIVALS).map((k) => k.prefix)),
];
export const STORES_NUMBER_PAD = 4;

/** The list's three pills. Internal Return holds both of its kinds. */
export const STORES_ARRIVAL_GROUPS = [
  { value: "vendor", label: "From Vendor", kinds: ["arrival_vendor"] },
  {
    value: "stock_transfer",
    label: "From Stock Transfer",
    kinds: ["arrival_stock_transfer"],
  },
  {
    value: "return",
    label: "Internal Return",
    kinds: ["return_own", "return_external"],
  },
] as const satisfies readonly {
  value: string;
  label: string;
  kinds: readonly StoresArrivalKind[];
}[];
export type StoresArrivalGroup =
  (typeof STORES_ARRIVAL_GROUPS)[number]["value"];

export const STORES_DOC_TABS = ["draft", "submitted", "cancelled"] as const;
export type StoresDocTab = (typeof STORES_DOC_TABS)[number];

/**
 * The same words on every Stores screen (user, 2026-10-30: "standardise things in Stores"): a
 * document is Draft, Previewed, Submitted or Cancelled, whatever its kind; a Cycle Inventory awaiting
 * approval is Previewed too. The Draft tab holds previewed documents as well.
 */
export type StoresDocStatus = "draft" | "previewed" | "submitted" | "cancelled";
export const STORES_STATUS_LABEL: Record<StoresDocStatus, string> = {
  draft: "Draft",
  previewed: "Previewed",
  submitted: "Submitted",
  cancelled: "Cancelled",
};
export const STORES_DOC_TAB_LABEL: Record<StoresDocTab, string> = {
  draft: "Draft",
  submitted: "Submitted",
  cancelled: "Cancelled",
};

/** "Return from Stage" on an Own return. seedtrac's list shows Packing; the rest are the plant's floors. */
export const STORES_RETURN_STAGES = [
  "Packing",
  "Processing",
  "Dispatch",
  "Production",
  "Quality Control",
  "Other",
] as const;

export const STORES_PARTY_CATEGORIES = [
  { value: "cnf", label: "C&F" },
  { value: "dealer", label: "Dealer" },
  { value: "other", label: "Other" },
  // seedtrac's Stock Transfer parties that are not NexSeed plants (user, 2026-10-13: Medchal,
  // Varanasi, Himmatnagar, Kohadia, VSPL...): Arrival / Issue › Stock Transfer may name one instead
  // of a plant. No STN link on their side; they are not on NexSeed.
  { value: "stock_transfer", label: "Stock Transfer" },
  // The units an Internal Return – Own can come back from (seedtrac: Gomchi Plant, Deorjhal,
  // VSPL-Raipur).
  { value: "own_unit", label: "Own unit" },
] as const;
/** Categories that need no address: our own units and transfer points, named as seedtrac lists them. */
export const STORES_PARTY_NO_ADDRESS: readonly string[] = [
  "stock_transfer",
  "own_unit",
];
export type StoresPartyCategory =
  (typeof STORES_PARTY_CATEGORIES)[number]["value"];
export const storesPartyCategoryLabel = (
  v: string | null | undefined
): string => STORES_PARTY_CATEGORIES.find((c) => c.value === v)?.label ?? "";

export type StoresCondition = "good" | "damage";

/** One put-away row: so many UPS / Qty of the line's Good or Damage into one leaf bin. */
export interface StoresSlocSplit {
  condition: StoresCondition;
  locationId: number;
  ups: number;
  qty: DecimalInput;
}

export interface StoresArrivalLineInput {
  itemId: number | null | undefined;
  dcUps?: number | null | undefined;
  dcQty?: DecimalInput;
  goodUps?: number | null | undefined;
  goodQty?: DecimalInput;
  damageUps?: number | null | undefined;
  damageQty?: DecimalInput;
  slocs: readonly StoresSlocSplit[];
}

const n = (v: number | null | undefined) => v ?? 0;
const d = (v: DecimalInput) => toDecimal(v ?? 0);

/**
 * Excess / Shortage: what arrived (Good + Damage) less what the DC says. Positive is excess,
 * negative shortage. Only meaningful for kinds with a DC.
 */
export function storesExcess(l: StoresArrivalLineInput): {
  ups: number;
  qty: string;
} {
  return {
    ups: n(l.goodUps) + n(l.damageUps) - n(l.dcUps),
    qty: d(l.goodQty).plus(d(l.damageQty)).minus(d(l.dcQty)).toFixed(3),
  };
}

/** The put-away totals of one condition. */
export function slocTotals(
  slocs: readonly StoresSlocSplit[],
  condition: StoresCondition
): { ups: number; qty: string } {
  let ups = 0;
  let qty = toDecimal(0);
  for (const s of slocs)
    if (s.condition === condition) {
      ups += s.ups;
      qty = qty.plus(d(s.qty));
    }
  return { ups, qty: qty.toFixed(3) };
}

/** What still has no bin, per condition: received less put away (UPS, Qty). */
export function slocRemaining(
  l: StoresArrivalLineInput,
  condition: StoresCondition
): { ups: number; qty: string } {
  const t = slocTotals(l.slocs, condition);
  const ups = condition === "good" ? n(l.goodUps) : n(l.damageUps);
  const qty = condition === "good" ? d(l.goodQty) : d(l.damageQty);
  return { ups: ups - t.ups, qty: qty.minus(t.qty).toFixed(3) };
}

/** Why one line cannot be posted yet, in the order the Post Item Form reads. Empty when it can. */
export function storesLineProblems(
  kind: StoresArrivalKind,
  l: StoresArrivalLineInput
): string[] {
  const cfg = storesArrival(kind);
  const out: string[] = [];
  if (!l.itemId) out.push("Stores Item");
  const neg =
    [l.dcUps, l.goodUps, l.damageUps].some((v) => n(v) < 0) ||
    [l.dcQty, l.goodQty, l.damageQty].some((v) => d(v).lt(0));
  if (neg) out.push("quantities cannot be negative");
  if (cfg.hasDc && (n(l.dcUps) <= 0 || d(l.dcQty).lte(0)))
    out.push("UPS and Quantity as per D.C.");
  if (!cfg.allowGood && (n(l.goodUps) > 0 || d(l.goodQty).gt(0)))
    out.push("an External Party return takes Damage only");
  const received =
    n(l.goodUps) + n(l.damageUps) > 0 ||
    d(l.goodQty).plus(d(l.damageQty)).gt(0);
  if (!received)
    out.push(cfg.allowGood ? "Good or Damage quantity" : "Damage quantity");
  // UPS and Qty travel together: a count with no weight (or the reverse) is a typing slip.
  for (const [ups, qty, label] of [
    [l.goodUps, l.goodQty, "Good"],
    [l.damageUps, l.damageQty, "Damage"],
  ] as const)
    if (n(ups) > 0 !== d(qty).gt(0))
      out.push(`${label} needs both UPS and Quantity`);
  for (const s of l.slocs)
    if (s.ups < 0 || d(s.qty).lt(0) || (s.ups === 0 && d(s.qty).isZero()))
      out.push("each SLOC row needs a UPS or Quantity");
  for (const c of ["good", "damage"] as const) {
    const r = slocRemaining(l, c);
    if (r.ups !== 0 || !toDecimal(r.qty).isZero())
      out.push(
        `${c === "good" ? "Good" : "Damage"} SLOC must add up to the ${c === "good" ? "Good" : "Damage"} received`
      );
  }
  const seen = new Set<string>();
  for (const s of l.slocs) {
    const k = `${s.condition}:${s.locationId}`;
    if (seen.has(k)) out.push("a bin appears twice in the SLOC split");
    seen.add(k);
  }
  return [...new Set(out)];
}

export interface StoresArrivalHeader {
  kind: StoresArrivalKind;
  vendorId?: number | null | undefined;
  sourceFacilityId?: number | null | undefined;
  partyId?: number | null | undefined;
  dcNo?: string | null | undefined;
  invoiceNo?: string | null | undefined;
  stnNo?: string | null | undefined;
  returnStage?: string | null | undefined;
  returnedByUserId?: number | null | undefined;
  returnedByText?: string | null | undefined;
  transit?: DispatchTransport | null | undefined;
}

const blank = (v: string | null | undefined) => !(v ?? "").trim();

/** What From Vendor asks for when neither of the challan's numbers is typed. */
export const STORES_DC_OR_INVOICE = "DC No. or Invoice No.";

/** The header's red-asterisk fields still blank, in screen order. */
export function storesHeaderMissing(h: StoresArrivalHeader): string[] {
  const out: string[] = [];
  switch (h.kind) {
    case "arrival_vendor":
      if (!h.vendorId) out.push("Vendor");
      // Either paper will do (user, 2026-10-08): the DC No. or the Invoice No.
      if (blank(h.dcNo) && blank(h.invoiceNo)) out.push(STORES_DC_OR_INVOICE);
      break;
    case "arrival_stock_transfer":
      // One of our plants, or a Stock Transfer party that is not on NexSeed.
      if (!h.sourceFacilityId && !h.partyId) out.push("Name of Party");
      if (blank(h.stnNo)) out.push("STN No.");
      break;
    case "return_own":
      // Which of our units it comes back from (seedtrac's Party Name).
      if (!h.partyId) out.push("Party Name");
      if (blank(h.returnStage)) out.push("Return from Stage");
      // "Other" must say which stage (user, 2026-11-05).
      else if (h.returnStage!.trim() === "Other")
        out.push("Return from Stage: name the other stage");
      if (!h.returnedByUserId && blank(h.returnedByText)) out.push("Return By");
      break;
    case "return_external":
      if (!h.partyId) out.push("Party Name");
      break;
  }
  if (storesArrival(h.kind).hasTransit)
    out.push(...missingDispatchTransport(h.transit ?? {}));
  return out;
}

/** What stops Preview: the header's required fields, at least one item, and every line whole. */
export function storesArrivalBlockers(
  h: StoresArrivalHeader,
  lines: readonly StoresArrivalLineInput[]
): string[] {
  const out = storesHeaderMissing(h);
  if (!lines.length) out.push("at least one item");
  lines.forEach((l, i) => {
    for (const p of storesLineProblems(h.kind, l))
      out.push(`Item ${i + 1}: ${p}`);
  });
  return out;
}

/** "UPS" counts are whole units; Qty is kept to the item's UoM decimals (Number 0, Kg 3...). */
export function fitsUomDecimals(qty: DecimalInput, decimals: number): boolean {
  const q = toDecimal(qty ?? 0);
  return q.decimalPlaces() <= decimals;
}
